import { Campaign, CampaignType, Collab, Influencer, ClientWithLocations, CampaignClientPair, FoodfluenceLocation } from '@foodfluence/shared';
import { getClientIdFromCollabRequest, getCampaignIdsWithActiveCollab, isBrandManagedCampaignType, isRetailCampaignType, isShippingCampaignType, isLocationfulCampaignType } from 'utils/collabUtils';
import { isCreatorUGCOnly } from 'utils/influencerUtils';
import { getUgcPresetCounts } from 'utils/contentUtils';
import { CollabFormValues } from './types';
import { getCurrentLocation, getGeneralLocationDisplay, getLocationDisplay } from 'utils/locationUtils';
import { getUgcRateDisplayName } from 'utils/contentUtils';
import deliverableNames from 'utils/deliverableNames';
import { toDate } from 'utils/dateUtils';

// Original utility methods
export const SINGLE_CLIENT_COLLAB_REQUEST_LIMIT = 2;

/** Placeholder pre-request collab so CreativeBrief can render a campaign before anything is sent. */
export const generateCollab = (campaign: Campaign): Collab => ({
  id: campaign.id,
  credit: campaign.credit || 0,
  cash: campaign.cash || 0,
  deliverableCounts: campaign.deliverableCounts || {},
  status: 'pendingInfluencerApproval',
  influencerID: '',
  influencerCampaignID: campaign.id,
  logs: [],
});

// ---- Campaign card / detail display helpers ----

export const CAMPAIGN_TYPE_CHIP: Record<CampaignType, string> = {
  inStore: 'In-store',
  retail: 'Retail',
  shipToHome: 'Delivered',
  shopify: 'Delivered',
  brandManaged: 'With brand',
};

/** "1x TikTok · 2x IG Reel" for social deliverables; UGC campaigns fall back to their content requirements. */
export const getDeliverableLines = (campaign: Campaign | null, counts?: Record<string, number>): string[] => {
  const social = Object.entries(counts || {})
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([type, count]) => `${count}x ${deliverableNames[type]?.short ?? type}`);
  if (social.length > 0) return social;
  // One line per kind of content: eight "Short Form" requirements read "8x Short Form", not eight lines
  const totals = new Map<string, number>();
  Object.values(campaign?.contentRequirements || {}).forEach(req => {
    const name = getUgcRateDisplayName({ type: req.type, duration: req.duration ?? null, count: req.count });
    const qty = req.type === 'video' ? req.count ?? 1 : 1;
    totals.set(name, (totals.get(name) ?? 0) + qty);
  });
  return Array.from(totals, ([name, qty]) => (qty > 1 ? `${qty}x ${name}` : name));
};

/** Short "where" line: ships to you, nearest location(s), or the brand's general areas. */
export const getCampaignWhereText = (
  campaign: (Campaign & { locations?: FoodfluenceLocation[] }) | null,
  referenceLocation?: { lat: number; lng: number }
): string => {
  if (!campaign) return '';
  if (isShippingCampaignType(campaign.campaignType)) return 'Ships to you';
  if (campaign.foodfluenceLocations?.length && campaign.locations?.length) {
    return getLocationDisplay(campaign.locations, referenceLocation);
  }
  if (campaign.allowedLocations && Object.keys(campaign.allowedLocations).length > 0) {
    return getGeneralLocationDisplay(campaign.allowedLocations);
  }
  return '';
};

/** Credit is an in-store-only reward; other types show it only if a value was set anyway. */
export const showsCredit = (campaignType: CampaignType | undefined, credit: number | undefined): boolean =>
  campaignType === 'inStore' || (credit ?? 0) > 0;

// ---- Explore feed: tabs mapped onto the section data ----

export type ExploreTab = 'all' | 'inStore' | 'retail' | 'ship';

/** Campaign types. Who the campaign is for (influencer or UGC) is a separate filter: any type can be either. */
export const EXPLORE_TABS: { key: ExploreTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'inStore', label: 'In-store' },
  { key: 'retail', label: 'Retail' },
  { key: 'ship', label: 'Delivered' },
];

type TypeTab = 'inStore' | 'retail' | 'ship';
const TYPE_SECTIONS: Record<TypeTab, { influencer: CampaignSectionKey; ugc: CampaignSectionKey }> = {
  inStore: { influencer: 'inStoreCampaigns', ugc: 'inStoreUGCCampaigns' },
  retail: { influencer: 'retailCampaigns', ugc: 'retailUGCCampaigns' },
  ship: { influencer: 'shipToHomeCampaigns', ugc: 'shipToHomeUGCCampaigns' },
};
const TYPE_TABS: TypeTab[] = ['inStore', 'retail', 'ship'];

/** Type sections then the active section (every type, incl. brand-managed, which has no tab); buildFeed drops repeats. */
const sectionsFor = (creator: 'influencer' | 'ugc'): CampaignSectionKey[] => [
  ...TYPE_TABS.map(t => TYPE_SECTIONS[t][creator]),
  creator === 'ugc' ? 'activeUGCCampaigns' : 'activeCampaigns',
];

/**
 * Sections behind a type tab and the UGC-only switch, in feed order (influencer before UGC).
 * UGC-only creators only get UGC sections from the backend, so their filter is always UGC.
 */
export const getFeedSections = (tab: ExploreTab, ugcOnly: boolean, canSeeInfluencer: boolean): CampaignSectionKey[] => {
  const creators: ('influencer' | 'ugc')[] =
    !canSeeInfluencer || ugcOnly ? ['ugc'] : ['influencer', 'ugc'];
  return creators.flatMap(c => (tab === 'all' ? sectionsFor(c) : [TYPE_SECTIONS[tab][c]]));
};

export type ExploreSort = 'recommended' | 'pay' | 'newest';

export const EXPLORE_SORTS: { key: ExploreSort; label: string }[] = [
  { key: 'recommended', label: 'Recommended' },
  { key: 'pay', label: 'Highest paying' },
  { key: 'newest', label: 'Newest' },
];

const createdAt = (campaign: Campaign): number => campaign.createdTime ?? toDate(campaign.startDate)?.getTime() ?? 0;

/** Recommended keeps the server order; the others sort what has loaded so far. */
export const sortFeed = (items: CampaignClientPair[], sort: ExploreSort): CampaignClientPair[] => {
  if (sort === 'recommended') return items;
  const sorted = [...items];
  if (sort === 'pay') {
    sorted.sort((a, b) => ((b.campaign.cash ?? 0) + (b.campaign.credit ?? 0)) - ((a.campaign.cash ?? 0) + (a.campaign.credit ?? 0)));
  } else {
    sorted.sort((a, b) => createdAt(b.campaign) - createdAt(a.campaign));
  }
  return sorted;
};

/**
 * Feed items, de-duplicated by campaign id. Sections are revealed one at a time: a section's
 * successors stay hidden until it has no more pages, so the next page always lands at the end
 * of the feed instead of being inserted above the reader.
 */
export const buildFeed = (
  sectionsData: SectionsData,
  sections: CampaignSectionKey[]
): { items: CampaignClientPair[]; nextSection: CampaignSectionKey | undefined } => {
  const seen = new Set<string>();
  const items: CampaignClientPair[] = [];
  for (const key of sections) {
    const section = sectionsData[key] as CampaignSection;
    section.data.forEach(pair => {
      if (seen.has(pair.campaign.id)) return;
      seen.add(pair.campaign.id);
      items.push(pair);
    });
    if (section.pagination.hasMore) return { items, nextSection: key };
  }
  return { items, nextSection: undefined };
};

export const findExistingCollabRequest = (
  campaign: Campaign | null,
  collabData: Collab[]
): Collab | null => {
  if (!campaign) return null;
  return collabData.find(collab => collab.influencerCampaignID === campaign.id) || null;
};

/**
 * Filters campaigns for display on ClientPage: excludes campaigns where influencer
 * has an active collab with this client, and applies creator type filtering (UGC/influencer).
 * TODO: Migrate to backend - getClientAndCampaigns should exclude campaigns where influencer has active collab
 */
export const filterAvailableCampaignsForClient = <T extends Campaign>(
  campaigns: T[],
  collabs: Collab[],
  clientId: string,
  influencerData?: Influencer | null
): T[] => {
  const collabsForClient = collabs.filter(collab => collab.clientID === clientId);
  const campaignIdsWithActiveCollab = getCampaignIdsWithActiveCollab(collabsForClient);
  return campaigns.filter(campaign => {
    if (campaignIdsWithActiveCollab.has(campaign.id)) return false;
    if (isCreatorUGCOnly(influencerData)) {
      return campaign.creatorType === 'ugc';
    }
    // Influencers (and those with both types) can apply for all campaigns including UGC
    return true;
  });
};

export const getPendingCollabRequestsForClient = (
  collabData: Collab[],
  campaigns: Record<string, Campaign>,
  client: { id: string } | null
): Collab[] => {
  return collabData.filter(collab => 
    collab.status === 'pendingClientApproval' &&    
    getClientIdFromCollabRequest(collab, campaigns?.[collab.id]) === client?.id
  );
};

export const hasCollabChanges = (
  values: CollabFormValues,
  requestedCollab: Collab | null,
  isCustomRequest: boolean,
  selectedLocationIds?: string[]
): boolean => {
  if (!requestedCollab) return true;

  const currentDeliverables = {
    tiktoks: values.tikTokCount,
    instagramReels: values.igReelCount,
    instagramPosts: values.igPostCount,
    instagramStories: values.igStoryCount
  };

  const originalDeliverables = requestedCollab.proposedInfluencerDeliverableCounts || {};

  const hasDeliverableChanges = (
    currentDeliverables.tiktoks !== (originalDeliverables.tiktoks || 0) ||
    currentDeliverables.instagramReels !== (originalDeliverables.instagramReels || 0) ||
    currentDeliverables.instagramPosts !== (originalDeliverables.instagramPosts || 0) ||
    currentDeliverables.instagramStories !== (originalDeliverables.instagramStories || 0)
  );

  const hasCompensationChanges = (
    values.cashAmount !== (requestedCollab.proposedInfluencerCash || 0) ||
    values.creditAmount !== (requestedCollab.proposedInfluencerCredit || 0)
  );

  const hasCustomRequestChanges = isCustomRequest && (
    values.note !== (requestedCollab.influencerNote || '') ||
    values.campaignType !== requestedCollab.customCollabType
  );

  const hasLocationChanges = isCustomRequest &&
    selectedLocationIds !== undefined &&
    JSON.stringify([...selectedLocationIds].sort()) !==
    JSON.stringify([...(requestedCollab.proposedSelectedLocationIds ?? [])].sort());

  let hasUgcDeliverableChanges = false;
  if (values.ugcPresetCounts) {
    const originalUgcCounts = getUgcPresetCounts(requestedCollab.proposedContent);
    const presetNames = new Set([...Object.keys(values.ugcPresetCounts), ...Object.keys(originalUgcCounts)]);
    hasUgcDeliverableChanges = Array.from(presetNames).some(
      name => (values.ugcPresetCounts?.[name] || 0) !== (originalUgcCounts[name] || 0)
    );
  }

  return hasDeliverableChanges || hasUgcDeliverableChanges || hasCompensationChanges || hasCustomRequestChanges || hasLocationChanges;
};

export const isCollabValid = (
  values: CollabFormValues,
  requestedCollab: Collab | null,
  pendingRequestsCount: number,
  campaign?: Campaign | null
): boolean => {
  const hasValidRequestLimit = (!requestedCollab && pendingRequestsCount < SINGLE_CLIENT_COLLAB_REQUEST_LIMIT) || requestedCollab;
  const isCustomBrandManagedRequest = !campaign && isBrandManagedCampaignType(values.campaignType);
  const hasValidCompensation = isCustomBrandManagedRequest
    ? values.cashAmount > 0 || values.creditAmount > 0
    : values.cashAmount > 0 ||
      values.creditAmount > 0 ||
      isShippingCampaignType(values.campaignType) ||
      isRetailCampaignType(values.campaignType);
  
  // Custom UGC proposals: deliverables are preset content units, not social posts
  if (values.ugcPresetCounts) {
    const hasUgcDeliverables = Object.values(values.ugcPresetCounts).some(count => count > 0);
    return hasValidRequestLimit && hasUgcDeliverables && hasValidCompensation;
  }

  // For content-based campaigns (UGC), we don't require deliverables
  if (values.isContentBased === true) {
    return hasValidRequestLimit && hasValidCompensation;
  }

  // For regular campaigns, require at least one deliverable
  const hasValidDeliverables = values.tikTokCount > 0 ||
    values.igReelCount > 0 ||
    values.igPostCount > 0 ||
    values.igStoryCount > 0;

  return hasValidRequestLimit && hasValidDeliverables && hasValidCompensation;
};

// New utility types and methods for explore page
export type ClientSectionKey = 'featuredBrands' | 'allBrands';
export type CampaignSectionKey = 
  | 'activeCampaigns'
  | 'activeUGCCampaigns'
  | 'inStoreCampaigns'
  | 'inStoreUGCCampaigns'
  | 'retailCampaigns'
  | 'retailUGCCampaigns'
  | 'shipToHomeCampaigns'
  | 'shipToHomeUGCCampaigns';

export type SectionKey = ClientSectionKey | CampaignSectionKey;

export interface SectionPaginationState {
  cursor: string | null;
  loading: boolean;
  hasMore: boolean;
}

export interface SectionData<T> {
  data: T[];
  pagination: SectionPaginationState;
}

export type ClientSection = SectionData<ClientWithLocations>;
export type CampaignSection = SectionData<CampaignClientPair>;

export type SectionsData = {
  [K in SectionKey]: K extends ClientSectionKey ? ClientSection : CampaignSection;
};

export const getInitialSectionsData = (): SectionsData => {
  // A fresh object per section: shared ones let a write to one section show up in all of them.
  const initialSection = () => ({
    data: [],
    pagination: { cursor: null, loading: false, hasMore: true },
  });

  return {
    featuredBrands: initialSection(),
    allBrands: initialSection(),
    activeCampaigns: initialSection(),
    activeUGCCampaigns: initialSection(),
    inStoreCampaigns: initialSection(),
    inStoreUGCCampaigns: initialSection(),
    retailCampaigns: initialSection(),
    retailUGCCampaigns: initialSection(),
    shipToHomeCampaigns: initialSection(),
    shipToHomeUGCCampaigns: initialSection()
  };
};

export const hasNoResults = (sectionsData: SectionsData): boolean => {
  return SECTION_ORDER.every(section => 
    !sectionsData[section].data || sectionsData[section].data.length === 0
  );
};

export const SECTION_DISPLAY_NAMES: Record<SectionKey, string> = {
  featuredBrands: 'Featured Brands',
  allBrands: 'New Brands',
  activeCampaigns: 'Active Influencer Campaigns',
  activeUGCCampaigns: 'Active UGC Campaigns',
  inStoreCampaigns: 'In-Store Influencer Campaigns',
  inStoreUGCCampaigns: 'In-Store UGC Campaigns',
  retailCampaigns: 'Retail Influencer Campaigns',
  retailUGCCampaigns: 'Retail UGC Campaigns',
  shipToHomeCampaigns: 'Ship-to-Home Influencer Campaigns',
  shipToHomeUGCCampaigns: 'Ship-to-Home UGC Campaigns'
};

// Define the order of sections
export const SECTION_ORDER: SectionKey[] = [
  'inStoreCampaigns',
  'inStoreUGCCampaigns',
  'retailCampaigns',
  'retailUGCCampaigns',
  'shipToHomeCampaigns',
  'shipToHomeUGCCampaigns',
  'featuredBrands',
  'activeCampaigns',
  'activeUGCCampaigns',
  'allBrands'
];

// Load filters from localStorage
export const loadSavedFilters = () => {
  try {
    const savedFilters = localStorage.getItem('explore_filters');
    if (savedFilters) {
      return JSON.parse(savedFilters);
    }
  } catch (e) {
    console.error('Error parsing saved filters', e);
  }
  return {
    searchQuery: '',
    location: null,
    searchRadius: 25
  };
};

// Format location display
export const formatLocationDisplay = (location: { city?: string; state?: string; lat: number; lng: number; radius: number }) => {
  if (location.city && location.state) {
    return `${location.city}, ${location.state}`;
  }
  return `${location.lat.toFixed(3)}, ${location.lng.toFixed(3)}`;
};

export interface CampaignSectionResult {
  section: CampaignSectionKey;
  data: CampaignClientPair[];
  cursor: string | null;
  hasMore: boolean;
}

export interface ClientSectionResult {
  section: ClientSectionKey;
  data: ClientWithLocations[];
  cursor: string | null;
  hasMore: boolean;
}

export type SectionResult = CampaignSectionResult | ClientSectionResult;

export const isCampaignSection = (section: SectionKey): section is CampaignSectionKey => {
  return [
    'activeCampaigns',
    'activeUGCCampaigns',
    'inStoreCampaigns',
    'inStoreUGCCampaigns',
    'retailCampaigns',
    'retailUGCCampaigns',
    'shipToHomeCampaigns',
    'shipToHomeUGCCampaigns'
  ].includes(section);
};

export const isClientSection = (section: SectionKey): section is ClientSectionKey => {
  return ['featuredBrands', 'allBrands'].includes(section);
};

export const isCampaignSectionResult = (result: SectionResult): result is CampaignSectionResult => {
  return isCampaignSection(result.section);
};

export const createSectionResult = (
  section: SectionKey,
  data: (ClientWithLocations | CampaignClientPair)[],
  cursor: string | null,
  hasMore: boolean
): SectionResult => {
  if (isCampaignSection(section)) {
    return {
      section,
      data: data as CampaignClientPair[],
      cursor,
      hasMore
    };
  } else {
    return {
      section: section as ClientSectionKey,
      data: data as ClientWithLocations[],
      cursor,
      hasMore
    };
  }
};

// US state abbreviations
export const US_STATE_ABBREVIATIONS: { [key: string]: string } = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
  'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH',
  'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC',
  'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA',
  'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD', 'Tennessee': 'TN',
  'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA',
  'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC'
};

// Initialize location if needed
export const initializeLocation = async (currentFilters: ReturnType<typeof loadSavedFilters>) => {
  // Only try to get location if there isn't one already set
  if (!currentFilters.location) {
    try {
      const currentLocation = await getCurrentLocation();
      if (currentLocation) {
        // Abbreviate state if in US
        let stateAbbr = currentLocation.state;
        if (currentLocation.country === 'United States' && currentLocation.state) {
          stateAbbr = US_STATE_ABBREVIATIONS[currentLocation.state] || currentLocation.state;
        }
        
        // Return new filters with current location
        return {
          ...currentFilters,
          location: {
            lat: currentLocation.lat,
            lng: currentLocation.lng,
            radius: currentFilters.searchRadius,
            fullAddress: `${currentLocation.city}${stateAbbr ? `, ${stateAbbr}` : ''}, ${currentLocation.country}`,
            streetNumber: '',
            streetName: '',
            city: currentLocation.city || '',
            state: stateAbbr || '',
            country: currentLocation.country || '',
            zip: ''
          }
        };
      }
    } catch (error) {
      console.error('Error getting current location:', error);
    }
  }
  
  // Return original filters if no changes needed
  return currentFilters;
};

/** The brand's own picture for a campaign tile: campaign image, then the brand banner, then its Instagram photo. */
export const getBrandImage = (
  campaign?: Pick<Campaign, 'collabImage'> | null,
  client?: { bannerPicture?: string; instagramProfilePicture?: string } | null
): string | undefined => campaign?.collabImage || client?.bannerPicture || client?.instagramProfilePicture || undefined;
